import { useState } from "react";
import { Popover, Modal, Form, Input, Button, Tabs } from "antd";
import {
  LogoutOutlined,
  SettingOutlined,
  MailOutlined,
  LockOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { base_url } from "../../utils/apiConfig";
import { handleError, handleSuccess } from "../../utils/Service/utils";
import "../../styles/ProfileModal.css";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "U";

const ProfileModal = ({
  client_name,
  clientData,
  email,
  onLogout,
  loggingOut,
  onEmailNotification,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Adjust these keys to match your userDetails.client payload
  const clientName = client_name || clientData?.name || "Client L";
  const clientEmail = email || clientData?.email || "";

  console.log("client_name-----", client_name);
  const openProfile = () => {
    setPopoverOpen(false);
    setProfileOpen(true);
  };

  const closeProfile = () => {
    setProfileOpen(false);
    form.resetFields();
  };

  const handleChangePassword = async (values) => {
    const token = localStorage.getItem("authToken");
    if (!token) return handleError("Session expired. Please log in again.");

    setSaving(true);
    try {
      const response = await axios.post(
        `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/change_password_api`,
        {
          old_password: values.currentPassword,
          new_password: values.newPassword,
        },
        { headers: { Authorization: `Token ${token}` } },
      );
      if (response.status === 200) {
        handleSuccess("Password updated successfully");
        closeProfile();
      } else {
        handleError(response?.non_error_field || "Couldn't update password");
      }
    } catch (error) {
      handleError(error?.non_error_field || "Couldn't update password");
    } finally {
      setSaving(false);
    }
  };

  const avatar = (size = "") => (
    <div className={`pm-avatar ${size}`}>
      {/* {logoUrl ? (
        <img src={logoUrl} alt="" />
      ) : ( */}
      <span>{getInitials(clientName)}</span>
      {/* )} */}
    </div>
  );

  const popoverContent = (
    <div className="pm-popover">
      <div className="pm-head">
        {avatar("pm-avatar-lg")}
        <div className="pm-head-text">
          <p className="pm-name" title={clientName}>
            {clientName}
          </p>
          {clientEmail && (
            <p className="pm-email" title={clientEmail}>
              {clientEmail}
            </p>
          )}
        </div>
      </div>

      <div className="pm-actions">
        <button type="button" className="pm-item" onClick={openProfile}>
          <SettingOutlined />
          <span>Manage profile</span>
        </button>
        <button
          type="button"
          className="pm-item pm-item-danger"
          onClick={onLogout}
          disabled={loggingOut}
        >
          <LogoutOutlined />
          <span>{loggingOut ? "Logging out…" : "Log out"}</span>
        </button>
      </div>
    </div>
  );

  const profileTab = (
    <div className="pm-profile-tab">
      <div className="pm-profile-top">
        {avatar("pm-avatar-lg")}
        <p className="pm-name">{clientName}</p>
      </div>

      <div className="pm-field">
        <span className="pm-field-label">
          <MailOutlined /> Email
        </span>
        <span className="pm-field-value">{clientEmail || "—"}</span>
      </div>

      <div>
        <Button
          block
          icon={<MailOutlined />}
          onClick={() => {
            setProfileOpen(false);
            onEmailNotification?.();
          }}
        >
          Manage Email Notifications
        </Button>
      </div>
    </div>
  );

  const passwordTab = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleChangePassword}
      requiredMark={false}
      className="pm-form"
    >
      <Form.Item
        label="Current password"
        name="currentPassword"
        rules={[{ required: true, message: "Enter your current password" }]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          autoComplete="current-password"
        />
      </Form.Item>
      <Form.Item
        label="New password"
        name="newPassword"
        rules={[
          { required: true, message: "Enter a new password" },
          { min: 8, message: "Use at least 8 characters" },
        ]}
      >
        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        label="Confirm new password"
        name="confirmPassword"
        dependencies={["newPassword"]}
        rules={[
          { required: true, message: "Confirm your new password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
      </Form.Item>
      <div className="pm-form-footer">
        <Button onClick={closeProfile}>Cancel</Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          className="pm-primary-btn"
        >
          Update password
        </Button>
      </div>
    </Form>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        placement="bottomRight"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        arrow={false}
        overlayClassName="pm-popover-overlay"
      >
        <button
          type="button"
          className="pm-trigger"
          aria-label="Open account menu"
        >
          {avatar()}
        </button>
      </Popover>

      <Modal
        title="Manage profile"
        open={profileOpen}
        onCancel={closeProfile}
        footer={null}
        width={440}
        centered
        destroyOnClose
        className="pm-modal"
      >
        <Tabs
          defaultActiveKey="profile"
          items={[
            { key: "profile", label: "Profile", children: profileTab },
            ...((localStorage.getItem("authToken") || "").startsWith("ci_") ? [] : [{
              key: "password",
              label: "Change password",
              children: passwordTab,
            }]),
          ]}
        />
      </Modal>
    </>
  );
};

export default ProfileModal;
